import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  exportPivotCsv,
  getPivotCustomerOptions,
  getPivotProducts,
  getPivotStores,
  getPivotVisitTimes,
  getPivotVisits,
  readPivotExportErrorMessage,
} from "../services/pivot";
import { useDebounce } from "../utils/hooks/useDebounce";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
  getPivotExportFilename,
} from "../components/Pivot/pivotExport";
import PivotFilterBar, { PivotFilterState } from "../components/Pivot/PivotFilterBar";
import {
  PivotBarButton,
  PivotTabs,
  PivotViewBar,
  PivotViewLabel,
} from "../components/Pivot/PivotToolbar";
import {
  PivotCard,
  PivotGapOverview,
  PivotSummaryOverview,
} from "../components/Pivot/PivotOverview";
import {
  PIVOT_VISITS_MAX_SPAN_DAYS,
  routeTimeSpanBlock,
  isVisitsSpanBlocked,
  usePivotBranchLocked,
} from "../components/Pivot/pivotLimits";
import PivotDataSourceNotice from "../components/Pivot/PivotDataSourceNotice";
import {
  pivotDataSourceNotice,
  readPivotExportDataSource,
} from "../components/Pivot/pivotDataSource";
import { usePivotRollupWatch } from "../components/Pivot/usePivotRollupWatch";
import {
  PIVOT_FILTER_DEBOUNCE_MS,
  usePivotSingleFlight,
} from "../components/Pivot/pivotSingleFlight";
import PivotGroupedTable from "../components/Pivot/PivotGroupedTable";
import PivotVisitTable from "../components/Pivot/PivotVisitTable";
import PivotRouteTimeView from "../components/Pivot/PivotRouteTime";
import { buildVisitTimeLookup } from "../components/Pivot/routeTimeFormat";
import {
  VisitTimesStatus,
  visitTimeColumnGroup,
  visitTimesBlockedMessage,
} from "../components/Pivot/pivotVisitTimeColumns";
import PivotProductPicker, {
  PivotProductOption,
  compareProductSequence,
} from "../components/Pivot/PivotProductPicker";
import {
  ProductStoresRows,
  ProductStoresBaseParams,
  useProductStoresDrill,
} from "../components/Pivot/PivotProductStoresDrill";
import {
  CodeChip,
  GROUP_LABEL,
  PIVOT_TAB_LABEL,
  rowUnit,
} from "../components/Pivot/pivotFormat";
import { PV } from "../components/Pivot/pivotTokens";
import {
  CustomerOptionsResponse,
  GapMinDays,
  GroupRef,
  Metrics,
  PivotDataSource,
  PivotExportView,
  PivotTab,
  ProductPivotResponse,
  ProductSortKey,
  StoreGroupBy,
  StorePivotResponse,
  StoreRow,
  storeGroupByForTab,
  StoreSortKey,
  VisitPivotResponse,
  VisitSortKey,
  VisitTimeRow,
} from "../types/pivot.type";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";

const EMPTY_METRICS: Metrics = {
  visit_count: 0,
  sold_qty: 0,
  sold_percent: null,
  waste_qty: null,
  waste_percent: null,
  carryover_qty: null,
  sales_amount: 0,
  sales_amount_incl_vat: 0,
};

const EMPTY_VISIT_TOTALS = {
  ...EMPTY_METRICS,
  planned_count: 0,
  visited_count: 0,
  missed_count: 0,
  unplanned_visit_count: 0,
  days_since_last_visit: 0,
  visited_by_date: {},
  missed_by_date: {},
};

const VISITS_SPAN_BLOCKED_MSG =
  `แท็บ “การเข้าร้าน” แบบรวมทุกสาขา ดูและดาวน์โหลดได้ไม่เกิน ${PIVOT_VISITS_MAX_SPAN_DAYS} วันต่อครั้ง — ` +
  `เลือกสาขา หรือย่อช่วงวันที่ให้ไม่เกิน ${PIVOT_VISITS_MAX_SPAN_DAYS} วัน แล้วลองใหม่`;

const EXPORT_FAILED_MSG = "ดาวน์โหลด CSV ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/**
 * The product tab opens in catalogue order — `product.sequence`, ascending, unsequenced SKUs
 * last (customer, 2026-10-08). Headers still re-sort; switching tab or รีเซ็ต comes back here.
 */
const defaultSort = (tab: PivotTab) =>
  tab === "visit" ? "days_since_last_visit" : tab === "sku" ? "sequence" : "sales_amount";

/** Catalogue order reads top-down; every metric reads biggest-first. */
const defaultSortDir = (key: string): "asc" | "desc" => (key === "sequence" ? "asc" : "desc");

/**
 * Where a store → products drill came from, so "กลับ" puts the user back on the same store
 * tab, sorted and folded the way they left it. The store itself rides along because the
 * ร้านค้า picker's options are capped and may not include it.
 */
interface StoreDrillOrigin {
  tab: PivotTab;
  sortBy: string;
  sortDir: "asc" | "desc";
  collapsed: Set<string>;
  q: string;
  customer: { code: string; name: string };
}

const selectSx = {
  height: 34,
  minWidth: 200,
  fontSize: 13.5,
  bgcolor: PV.card,
  borderRadius: PV.radiusSm,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: PV.line },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PV.indigoBorder },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PV.indigo },
  "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
} as const;

export default function PivotPage() {
  const [tab, setTab] = useState<PivotTab>("ctype");
  const [filters, setFilters] = useState<PivotFilterState>({
    // Opens on today, so the picker's preset pill reads "วันนี้" and the first query is the
    // cheapest one the page can ask for — the aggregation cost scales with the span.
    dateFrom: dayjs().startOf("day"),
    dateTo: dayjs().startOf("day"),
    branchCode: "all",
    routeCode: "all",
    customerTypes: [],
    customerGroups: [],
    q: "",
  });
  const debouncedQ = useDebounce(filters.q, 400);
  // The multi-select filters fire a request per tick otherwise — see pivotSingleFlight.
  const debouncedCustomerTypes = useDebounce(filters.customerTypes, PIVOT_FILTER_DEBOUNCE_MS);
  const debouncedCustomerGroups = useDebounce(filters.customerGroups, PIVOT_FILTER_DEBOUNCE_MS);

  const [visitGroupBy, setVisitGroupBy] = useState<StoreGroupBy>("route");
  const [gapMinDays, setGapMinDays] = useState<GapMinDays>(0);
  const [customerCode, setCustomerCode] = useState("ALL");
  const [customerOptions, setCustomerOptions] = useState<CustomerOptionsResponse | null>(null);
  const [drillOrigin, setDrillOrigin] = useState<StoreDrillOrigin | null>(null);
  const [productCodes, setProductCodes] = useState<string[]>([]);
  const debouncedProductCodes = useDebounce(productCodes, PIVOT_FILTER_DEBOUNCE_MS);
  /**
   * The product picker's options: the products the tab loaded for the current scope WITHOUT
   * the product filter (or a search) narrowing them — kept aside so picking one product does
   * not shrink the list to that one product.
   */
  const [productOptions, setProductOptions] = useState<PivotProductOption[]>([]);
  const [exportingProduct, setExportingProduct] = useState<string | null>(null);
  const [visitTimes, setVisitTimes] = useState<VisitTimeRow[]>([]);
  const [visitTimesStatus, setVisitTimesStatus] = useState<VisitTimesStatus>("loading");

  const [sortBy, setSortBy] = useState<string>("sales_amount");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const [storesData, setStoresData] = useState<StorePivotResponse | null>(null);
  const [productsData, setProductsData] = useState<ProductPivotResponse | null>(null);
  const [visitsData, setVisitsData] = useState<VisitPivotResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportDataSource, setExportDataSource] = useState<PivotDataSource | null>(null);
  // Bumped when a background rollup rebuild finishes, to pull the now-precomputed numbers.
  const [reloadKey, setReloadKey] = useState(0);
  // One request in flight per stream; see pivotSingleFlight for why.
  const tableFlight = usePivotSingleFlight();
  const visitTimesFlight = usePivotSingleFlight();
  const customerOptionsFlight = usePivotSingleFlight();

  const scopeParams = useMemo(
    () => ({
      date_from: filters.dateFrom.format("YYYY-MM-DD"),
      date_to: filters.dateTo.format("YYYY-MM-DD"),
      branch_code: filters.branchCode !== "all" ? filters.branchCode : undefined,
      route_code: filters.routeCode !== "all" ? filters.routeCode : undefined,
      customer_type: debouncedCustomerTypes.length ? debouncedCustomerTypes : undefined,
      customer_group: debouncedCustomerGroups.length ? debouncedCustomerGroups : undefined,
    }),
    [
      filters.dateFrom,
      filters.dateTo,
      filters.branchCode,
      filters.routeCode,
      debouncedCustomerTypes,
      debouncedCustomerGroups,
    ],
  );

  const exportView: PivotExportView =
    tab === "sku" ? "products" : tab === "visit" ? "visits" : "stores";

  const branchLocked = usePivotBranchLocked();

  /**
   * The backend refuses the visits view past `PIVOT_VISITS_MAX_SPAN_DAYS` when no branch
   * narrows it — on screen and as CSV alike, since both go through the same builder (see
   * `pivotLimits`). Check it here rather than round-tripping to find out: a rejected table
   * renders as an empty one, which reads as "no data" rather than "range too wide".
   */
  const visitsSpanBlocked = useMemo(
    () =>
      tab === "visit" &&
      isVisitsSpanBlocked(
        branchLocked,
        filters.branchCode,
        filters.dateFrom,
        filters.dateTo,
      ),
    [tab, branchLocked, filters.branchCode, filters.dateFrom, filters.dateTo],
  );

  useEffect(() => {
    // The backend would reject this scope before it touched the database. Stop here rather
    // than spend the round trip, and drop whatever the last scope loaded so the screen does
    // not show one range's numbers under another range's filter bar.
    if (visitsSpanBlocked) {
      setVisitsData(null);
      setLoading(false);
      return;
    }
    // เวลาวิ่งสาย loads its own data (PivotRouteTimeView).
    if (tab === "routetime") {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const isStale = () => cancelled;
    setLoading(true);

    (async () => {
      try {
        if (tab === "sku") {
          const res = await tableFlight(isStale, () =>
            getPivotProducts({
              ...scopeParams,
              customer_code: customerCode !== "ALL" ? customerCode : undefined,
              product_code: debouncedProductCodes.length ? debouncedProductCodes : undefined,
              q: debouncedQ || undefined,
              sort_by: sortBy as ProductSortKey,
              sort_dir: sortDir,
            }),
          );
          if (res && !cancelled) {
            const { data } = res;
            setProductsData(data.data);
            if (data.data && !debouncedProductCodes.length && !debouncedQ) {
              const seen = new Map<string, PivotProductOption>();
              data.data.groups.forEach((g) =>
                g.rows.forEach((r) => {
                  if (!seen.has(r.product_code))
                    seen.set(r.product_code, {
                      product_code: r.product_code,
                      product_name: r.product_name,
                      sequence: r.sequence ?? null,
                    });
                }),
              );
              setProductOptions(Array.from(seen.values()).sort(compareProductSequence));
            }
          }
        } else if (tab === "visit") {
          const res = await tableFlight(isStale, () =>
            getPivotVisits({
              ...scopeParams,
              group_by: visitGroupBy,
              gap_min_days: gapMinDays,
              q: debouncedQ || undefined,
              sort_by: sortBy as VisitSortKey,
              sort_dir: sortDir,
            }),
          );
          if (res && !cancelled) setVisitsData(res.data.data);
        } else {
          const res = await tableFlight(isStale, () =>
            getPivotStores({
              ...scopeParams,
              group_by: storeGroupByForTab(tab),
              q: debouncedQ || undefined,
              sort_by: sortBy as StoreSortKey,
              sort_dir: sortDir,
            }),
          );
          if (res && !cancelled) setStoresData(res.data.data);
        }
      } catch {
        // The shared axios interceptor already surfaced the reason (toast, or the Permission
        // Denied screen on a 403). Swallow it here so a failed load renders the table's empty
        // state instead of escaping as an unhandled rejection and tripping the dev overlay.
        if (!cancelled) {
          if (tab === "sku") setProductsData(null);
          else if (tab === "visit") setVisitsData(null);
          else setStoresData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    tab,
    scopeParams,
    debouncedQ,
    sortBy,
    sortDir,
    visitGroupBy,
    gapMinDays,
    customerCode,
    debouncedProductCodes,
    visitsSpanBlocked,
    reloadKey,
    tableFlight,
  ]);

  // PWA visit-time medians for the สายส่ง tab's time band — same scope as the stores call,
  // fetched alongside it (not after it) and merged on screen by customer_code. Search and
  // sort do not change which stores have times, so they are not in the deps.
  // The endpoint refuses more than 31 days (inclusive), or more than 7 across every branch:
  // past that the band stays "–" with a one-line note, and the rest of the tab loads as usual.
  const visitTimesBlock = useMemo(
    () => routeTimeSpanBlock(branchLocked, filters.branchCode, filters.dateFrom, filters.dateTo),
    [branchLocked, filters.branchCode, filters.dateFrom, filters.dateTo],
  );
  const visitTimesBlocked = visitTimesBlock !== null;

  useEffect(() => {
    if (tab !== "route") return;
    if (visitTimesBlocked) {
      setVisitTimes([]);
      setVisitTimesStatus("blocked");
      return;
    }
    let cancelled = false;
    setVisitTimesStatus("loading");
    (async () => {
      try {
        const res = await visitTimesFlight(
          () => cancelled,
          () => getPivotVisitTimes(scopeParams),
        );
        if (res && !cancelled) {
          setVisitTimes(res.data.data?.rows ?? []);
          setVisitTimesStatus("ok");
        }
      } catch {
        // Toasted by the interceptor; the band says it failed rather than showing "–",
        // which would read as "this store has no PWA data".
        if (!cancelled) {
          setVisitTimes([]);
          setVisitTimesStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, scopeParams, visitTimesBlocked, reloadKey, visitTimesFlight]);

  const visitTimeColumns = useMemo(
    () =>
      visitTimeColumnGroup(
        buildVisitTimeLookup(visitTimes),
        visitTimesStatus,
        visitTimesBlockedMessage(visitTimesBlock),
      ),
    [visitTimes, visitTimesStatus, visitTimesBlock],
  );

  // Store picker for the product tab — same scope as the table, minus q / customer_code.
  useEffect(() => {
    if (tab !== "sku") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await customerOptionsFlight(
          () => cancelled,
          () =>
            getPivotCustomerOptions({
              branch_code: scopeParams.branch_code,
              route_code: scopeParams.route_code,
              customer_type: scopeParams.customer_type,
              customer_group: scopeParams.customer_group,
            }),
        );
        if (res && !cancelled) setCustomerOptions(res.data.data);
      } catch {
        // Picker options are a convenience — losing them leaves "ทุกร้านในกลุ่มที่เลือก"
        // selectable and the table still loads, so fail quiet rather than break the tab.
        if (!cancelled) setCustomerOptions(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, scopeParams, customerOptionsFlight]);

  // A failure — and the verdict on the file that produced it — belongs to the scope it came
  // from. Drop both as soon as that scope moves.
  useEffect(() => {
    setExportError(null);
    setExportDataSource(null);
  }, [tab, scopeParams, visitGroupBy, gapMinDays, customerCode, debouncedProductCodes]);

  // One product's channel -> store tree under its row. Same scope as the product table
  // (including the picked store), minus the product filter, which the drill replaces.
  const productStoresBase: ProductStoresBaseParams = useMemo(
    () => ({ ...scopeParams, customer_code: customerCode !== "ALL" ? customerCode : undefined }),
    [scopeParams, customerCode],
  );
  const productDrill = useProductStoresDrill(productStoresBase);

  const onTabChange = useCallback((next: PivotTab) => {
    setTab(next);
    setDrillOrigin(null);
    setProductCodes([]);
    setFilters((f) => ({ ...f, q: "" }));
    setGapMinDays(0);
    setCustomerCode("ALL");
    setCollapsed(new Set());
    setSortBy(defaultSort(next));
    setSortDir(defaultSortDir(defaultSort(next)));
  }, []);

  const onReset = useCallback(() => {
    setDrillOrigin(null);
    setProductCodes([]);
    setFilters((f) => ({ ...f, q: "" }));
    setGapMinDays(0);
    setCustomerCode("ALL");
    setCollapsed(new Set());
    setSortBy(defaultSort(tab));
    setSortDir(defaultSortDir(defaultSort(tab)));
  }, [tab]);

  /**
   * Store row → that store's products. Same page, the product tab with `customerCode` set:
   * the date / branch / route / type / group filters are kept as they are (unlike a tab
   * switch, which clears the store), and the store tab's own view is parked for the way back.
   */
  const onStoreRowClick = useCallback(
    (row: StoreRow) => {
      setDrillOrigin({
        tab,
        sortBy,
        sortDir,
        collapsed,
        q: filters.q,
        customer: { code: row.customer_code, name: row.customer_name },
      });
      setTab("sku");
      setProductCodes([]);
      setFilters((f) => ({ ...f, q: "" }));
      setGapMinDays(0);
      setCustomerCode(row.customer_code);
      setCollapsed(new Set());
      setSortBy(defaultSort("sku"));
      setSortDir(defaultSortDir(defaultSort("sku")));
    },
    [tab, sortBy, sortDir, collapsed, filters.q],
  );

  const onBackToStores = useCallback(() => {
    if (!drillOrigin) return;
    setTab(drillOrigin.tab);
    setFilters((f) => ({ ...f, q: drillOrigin.q }));
    setCustomerCode("ALL");
    setCollapsed(drillOrigin.collapsed);
    setSortBy(drillOrigin.sortBy);
    setSortDir(drillOrigin.sortDir);
    setDrillOrigin(null);
  }, [drillOrigin]);

  const onSort = useCallback(
    (key: string) => {
      if (sortBy === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      else {
        setSortBy(key);
        setSortDir(defaultSortDir(key));
      }
    },
    [sortBy],
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Narrowed to the shape the fold-all control needs — the three tabs' group types differ
  // in their rows, and TS cannot call .some() across that union.
  const activeGroups: { group: GroupRef; is_flat?: boolean }[] = useMemo(
    () =>
      tab === "sku"
        ? (productsData?.groups ?? [])
        : tab === "visit"
          ? (visitsData?.groups ?? [])
          : (storesData?.groups ?? []),
    [tab, productsData, visitsData, storesData],
  );

  const isFlat = activeGroups.some((g) => g.is_flat);
  const allFolded = activeGroups.length > 0 && activeGroups.every((g) => collapsed.has(g.group.key));

  const toggleFoldAll = useCallback(() => {
    setCollapsed(allFolded ? new Set() : new Set(activeGroups.map((g) => g.group.key)));
  }, [allFolded, activeGroups]);

  const onExportCsv = async () => {
    // The button is already disabled in this state and the warning is already on screen —
    // this only keeps a programmatic call from firing a request the backend would reject.
    if (visitsSpanBlocked) return;
    setExporting(true);
    setExportError(null);
    setExportDataSource(null);
    try {
      const view = exportView;
      const response = await exportPivotCsv({
        view,
        ...scopeParams,
        group_by:
          tab === "visit"
            ? visitGroupBy
            : tab === "sku"
              ? undefined
              : storeGroupByForTab(tab),
        customer_code: tab === "sku" && customerCode !== "ALL" ? customerCode : undefined,
        product_code: tab === "sku" && debouncedProductCodes.length ? debouncedProductCodes : undefined,
        gap_min_days: tab === "visit" ? gapMinDays : undefined,
        q: debouncedQ || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        getPivotExportFilename(view);
      downloadBlob(filename, response.data);
      // The CSV has no JSON envelope, so its verdict rides on the X-Pivot-* headers. Worth
      // reading: the export is its own request and can land on a different path than the
      // table above it — a download that took a minute deserves the same explanation.
      setExportDataSource(readPivotExportDataSource(response.headers));
    } catch (error) {
      // The export opts out of the shared toast, so nothing else surfaces this. A 400 from
      // one of the span guards is a normal path (wide range, no branch), not an opaque 5xx,
      // so show the backend's own message inline instead of leaving the button to just stop
      // spinning. No partial file is written either way.
      setExportError((await readPivotExportErrorMessage(error)) || EXPORT_FAILED_MSG);
    } finally {
      setExporting(false);
    }
  };

  /** B3 — one product's (channel, store) rows as CSV, same scope as its drill. */
  const onExportProductStores = async (productCode: string) => {
    setExportingProduct(productCode);
    setExportError(null);
    try {
      const response = await exportPivotCsv({
        view: "product_stores",
        ...productStoresBase,
        product_code: [productCode],
      });
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        getPivotExportFilename(`product-stores-${productCode}`);
      downloadBlob(filename, response.data);
    } catch (error) {
      setExportError((await readPivotExportErrorMessage(error)) || EXPORT_FAILED_MSG);
    } finally {
      setExportingProduct(null);
    }
  };

  const flatCustomerOptions = useMemo(() => {
    const options: { customer_code: string; customer_name: string }[] =
      customerOptions?.routes.flatMap((r) =>
        r.customers.map((c) => ({ customer_code: c.customer_code, customer_name: c.customer_name })),
      ) ?? [];
    // The options list is capped, so the store a drill landed on may not be in it. Without
    // this the Select would hold a value it has no item for and render blank.
    const drilled = drillOrigin?.customer;
    if (drilled && !options.some((o) => o.customer_code === drilled.code)) {
      options.unshift({ customer_code: drilled.code, customer_name: drilled.name });
    }
    return options;
  }, [customerOptions, drillOrigin]);

  const activeData =
    tab === "sku"
      ? productsData
      : tab === "visit"
        ? visitsData
        : tab === "routetime"
          ? null
          : storesData;
  const wasteCapable = activeData?.capabilities.waste ?? true;
  const carryoverCapable = activeData?.capabilities.carryover ?? true;
  const unit = rowUnit(tab);

  /**
   * Which path answered this view. `ROLLUP` and `HYBRID` are the fast, normal cases and say
   * nothing on screen; `LIVE` means the aggregation ran against the fact rows instead of the
   * precomputed daily rollup — same numbers, long wait — and is the one worth explaining,
   * because otherwise the only signal the user gets is a page that sat there.
   */
  const dataSource = activeData?.data_source;
  const dataSourceNotice = pivotDataSourceNotice(dataSource);

  /**
   * Scope identity for the rebuild watch. A different question earns a fresh auto-refetch
   * budget; sort and search do not, since they cannot change which path answers.
   */
  const rollupScopeKey = `${tab}|${scopeParams.date_from}|${scopeParams.date_to}|${
    scopeParams.branch_code ?? "all"
  }`;

  const onRebuildFinished = useCallback(() => setReloadKey((k) => k + 1), []);

  const rollupWatch = usePivotRollupWatch({
    dataSource,
    branchCode: scopeParams.branch_code,
    resetKey: rollupScopeKey,
    onFinished: onRebuildFinished,
  });

  const exportNotice = pivotDataSourceNotice(exportDataSource);

  /**
   * The honest caveat on both percentages.
   *
   * `sold_percent` is `ขาย / (ขาย + ของเสีย)` and an unrecorded ของเสีย counts as 0 inside
   * it — so a store-day nobody recorded comes back at exactly 100%, which reads as "sold
   * everything" and means "we do not know". This banner is the only thing that says so, so
   * it is a warning rather than an info note.
   *
   * เหลือบนเชลล์ is reported separately because it is NOT in that denominator: a gap there
   * leaves a column blank but cannot bend a percentage.
   */
  const dataQualityNotice = useMemo(() => {
    const dq = activeData?.summary.data_quality;
    if (!dq || dq.total_count === 0) return null;

    const noun = dq.unit === "store_day" ? "วันที่เข้าร้าน" : "รายการสินค้า";
    const parts: string[] = [];
    if (dq.waste_unknown_count > 0) {
      parts.push(
        `ยังไม่มีการบันทึกของเสียใน ${dq.waste_unknown_count.toLocaleString("th-TH")} จาก ` +
          `${dq.total_count.toLocaleString("th-TH")} ${noun} — %ของขาย และ %ของเสีย ในช่วงนี้จึงอาจสูงกว่าความจริง`,
      );
    }
    if (dq.carryover_unknown_count > 0) {
      parts.push(
        `เหลือบนเชลล์ ยังไม่ถูกบันทึกใน ${dq.carryover_unknown_count.toLocaleString("th-TH")} ${noun} ` +
          `(แสดงเป็น “—” ไม่ใช่ 0 และไม่มีผลกับเปอร์เซ็นต์)`,
      );
    }
    return parts.length > 0 ? parts.join(" · ") : null;
  }, [activeData]);

  /**
   * The caveat on the กลุ่มลูกค้า filter, which is about the customer master rather than
   * about anything Pivot measures — hence a second banner rather than another clause in
   * the one above.
   *
   * Not every customer carries a group code. A store with none matches NO group, so the
   * moment a group is selected it silently leaves the report: totals drop and nobody is
   * told why. That has to be said before it happens, not after.
   *
   * The two branches exist because the count is only knowable in one of them. With no group
   * selected the ungrouped stores are still in `rows` and can be counted exactly. Once a
   * group IS selected the backend has already dropped them, so the result set cannot say how
   * many went — the warning stays, without a number it would have to invent.
   *
   * The product tab is skipped: its rows are products, and `customer_group_code` is not on
   * them. Counting distinct `customer_code` rather than rows keeps a store from being
   * counted twice if it ever appears under more than one group heading.
   */
  const ungroupedNotice = useMemo(() => {
    if (tab === "sku" || tab === "routetime") return null;

    if (filters.customerGroups.length > 0) {
      return "กำลังกรองด้วยกลุ่มลูกค้า — ร้านที่ยังไม่ได้ระบุกลุ่มจะไม่ถูกนับรวมในตัวเลขทั้งหมดบนหน้านี้";
    }

    const data = tab === "visit" ? visitsData : storesData;
    if (!data) return null;

    const ungrouped = new Set<string>();
    data.groups.forEach((g) =>
      g.rows.forEach((r) => {
        if (!r.customer_group_code) ungrouped.add(r.customer_code);
      }),
    );
    if (ungrouped.size === 0) return null;

    return (
      `มี ${ungrouped.size.toLocaleString("th-TH")} ร้านในช่วงนี้ที่ยังไม่ได้ระบุกลุ่มลูกค้า — ` +
      "ถ้าเลือกกรองด้วยกลุ่ม ร้านเหล่านี้จะหายไปจากรายงาน"
    );
  }, [tab, storesData, visitsData, filters.customerGroups]);
  const groupLabel =
    tab === "visit" ? GROUP_LABEL[visitGroupBy] : tab === "sku" ? "กลุ่ม" : GROUP_LABEL[storeGroupByForTab(tab)];

  // Only the store/product tabs carry `top_rows` + the KPI-shaped grand total; the visit
  // tab shows gap tiles instead, driven off `visitsData` directly.
  const summaryData: StorePivotResponse | ProductPivotResponse | null =
    tab === "sku" ? productsData : tab === "visit" || tab === "routetime" ? null : storesData;

  const drilledStoreActive =
    tab === "sku" && drillOrigin !== null && customerCode === drillOrigin.customer.code;

  /** The table card's one-line caption: the grid truncation, or how to read / drill. */
  let tableSub: string | undefined;
  if (tab === "visit") {
    tableSub = visitsData?.grid.is_truncated
      ? `ช่วงนี้ยาว ${visitsData.scope.span_days} วัน กริดแสดง ${visitsData.grid.day_count} วันล่าสุด (${visitsData.grid.date_from} เป็นต้นไป) · ตัวเลขสรุปและ CSV นับครบทุกวัน`
      : undefined;
  } else if (tab === "sku") {
    tableSub =
      (drilledStoreActive && drillOrigin
        ? `สินค้าของร้าน ${drillOrigin.customer.name} · ${drillOrigin.customer.code} · `
        : "") +
      "กดที่แถวสินค้าเพื่อดูช่องทางและร้านที่ขายได้ · รายชื่อร้านแสดงเฉพาะร้านที่ขายได้ (ขาย > 0)";
  } else if (tab === "route") {
    tableSub =
      (visitTimesBlock === "ho"
        ? `${visitTimesBlockedMessage(visitTimesBlock)} · `
        : visitTimesBlock === "max"
          ? `${visitTimesBlockedMessage(visitTimesBlock)} — ย่อช่วงวันที่เพื่อดูคอลัมน์เวลา · `
          : "") +
      "คอลัมน์เวลาเป็นมัธยฐานของทุกวันที่เข้าร้านในช่วงที่เลือก (วันผิดปกติวันเดียวจึงไม่ดึงค่าทั้งช่วง) · " +
      "มีเฉพาะร้านในสาย PWA ร้านอื่นแสดง “–” · กดที่แถวร้านเพื่อดูรายสินค้าของร้านนั้น";
  } else {
    tableSub = "กดที่แถวร้านเพื่อดูรายสินค้าของร้านนั้น";
  }

  const viewBarLeft =
    tab === "sku" ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {drillOrigin && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 0.5 }}>
            <PivotBarButton onClick={onBackToStores}>← กลับไปตารางรายร้าน</PivotBarButton>
            <Typography sx={{ fontSize: 12, color: PV.tx3 }}>
              {PIVOT_TAB_LABEL[drillOrigin.tab]}
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: 13, color: PV.tx2 }}>ร้านค้า</Typography>
        <Select
          size="small"
          value={customerCode}
          onChange={(e) => setCustomerCode(e.target.value)}
          sx={selectSx}
        >
          <MenuItem value="ALL">ทุกร้านในกลุ่มที่เลือก</MenuItem>
          {flatCustomerOptions.map((c) => (
            <MenuItem key={c.customer_code} value={c.customer_code}>
              {c.customer_name} · {c.customer_code}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ fontSize: 13, color: PV.tx2, ml: 1 }}>สินค้า</Typography>
        <PivotProductPicker
          options={productOptions}
          value={productCodes}
          onChange={setProductCodes}
        />
      </Box>
    ) : tab === "visit" ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: PV.indigoTint,
          border: "1px solid",
          borderColor: PV.indigoBorder,
          borderRadius: PV.radiusSm,
          pl: 1.5,
          pr: 0.75,
          py: 0.5,
        }}
      >
        <Typography sx={{ fontSize: 13, color: PV.indigo, fontWeight: 600 }}>จัดกลุ่มตาม</Typography>
        <Select
          size="small"
          value={visitGroupBy}
          onChange={(e) => setVisitGroupBy(e.target.value as StoreGroupBy)}
          sx={{ ...selectSx, minWidth: 150 }}
        >
          {(["customer_type", "route", "branch"] as StoreGroupBy[]).map((k) => (
            <MenuItem key={k} value={k}>
              {GROUP_LABEL[k]}
            </MenuItem>
          ))}
        </Select>
      </Box>
    ) : (
      <PivotViewLabel tab={tab} />
    );

  return (
    <Box sx={{ pb: 4 }}>
      {/* page head — title left, global filters pushed right, wrapping to their own row when narrow */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2.5,
          flexWrap: "wrap",
          mb: 2.25,
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" }}>
          Pivot — สรุปการขาย
        </Typography>
        <Box sx={{ flex: "1 1 560px", display: "flex", justifyContent: "flex-end" }}>
          <PivotFilterBar filters={filters} onChange={setFilters} />
        </Box>
      </Box>

      <PivotTabs tab={tab} onChange={onTabChange} />

      {tab === "routetime" ? (
        <PivotRouteTimeView filters={filters} />
      ) : (
        <>

          <PivotViewBar
            left={viewBarLeft}
            searchPlaceholder={
              tab === "sku"
                ? "ค้นหาชื่อสินค้า / รหัสสินค้า…"
                : tab === "visit"
                  ? "ค้นหาชื่อร้าน / รหัสร้านค้า / สายส่ง…"
                  : "ค้นหาชื่อร้าน / รหัสร้านค้า…"
            }
            q={filters.q}
            onSearch={(q) => setFilters((f) => ({ ...f, q }))}
            onToggleFoldAll={isFlat ? undefined : toggleFoldAll}
            allFolded={allFolded}
            onReset={onReset}
          />

          {dataSourceNotice && (
            <PivotDataSourceNotice
              message={dataSourceNotice.message}
              severity={dataSourceNotice.severity}
              watch={rollupWatch}
            />
          )}

          {dataQualityNotice && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: PV.radius }}>
              {dataQualityNotice}
            </Alert>
          )}

          {ungroupedNotice && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: PV.radius }}>
              {ungroupedNotice}
            </Alert>
          )}

          {(visitsSpanBlocked || exportError) && (
            <Alert
              severity={visitsSpanBlocked ? "warning" : "error"}
              sx={{ mb: 2, borderRadius: PV.radius }}
              onClose={visitsSpanBlocked ? undefined : () => setExportError(null)}
            >
              {visitsSpanBlocked ? VISITS_SPAN_BLOCKED_MSG : exportError}
            </Alert>
          )}

          {/* The export is its own request and can land on a different path than the table, so
              it gets its own line — but only when it says something the table has not already. */}
          {exportNotice && exportNotice.message !== dataSourceNotice?.message && (
            <PivotDataSourceNotice
              title="ไฟล์ CSV ที่ดาวน์โหลด"
              message={exportNotice.message}
              severity={exportNotice.severity}
              watch={null}
            />
          )}

          {tab === "visit" ? (
            <PivotGapOverview
              buckets={visitsData?.summary.gap_buckets ?? []}
              scopeStoreCount={visitsData?.summary.scope_store_count ?? 0}
              active={gapMinDays}
              onSelect={setGapMinDays}
              topRows={(visitsData?.summary.gap_top_rows ?? []).map((r) => ({
                label: r.customer_name,
                days: r.days_since_last_visit,
                capped: r.days_since_last_visit_is_capped,
              }))}
            />
          ) : (
            summaryData && (
              <PivotSummaryOverview
                title={`ภาพรวม${tab === "sku" ? "รายสินค้า" : `ตาม${groupLabel}`} · Top 10`}
                rowCount={summaryData.summary.row_count}
                unit={unit}
                grandTotal={summaryData.summary.grand_total}
                wasteCapable={wasteCapable}
                carryoverCapable={carryoverCapable}
                bars={summaryData.summary.top_rows.map((r) => ({
                  label: r.label ?? r.key ?? "ไม่ระบุ",
                  value: r.metrics.sold_qty,
                }))}
              />
            )
          )}

          <PivotCard
            flush
            title={
              tab === "sku"
                ? "ตารางสรุปรายสินค้า"
                : tab === "visit"
                  ? "ตารางการเข้าร้าน · รายร้าน × รายวัน"
                  : "ตารางสรุปรายร้าน"
            }
            sub={tableSub}
            action={
              <PivotBarButton
                onClick={onExportCsv}
                disabled={exporting || visitsSpanBlocked}
                startIcon={
                  exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon sx={{ fontSize: 16 }} />
                }
              >
                ดาวน์โหลด CSV
              </PivotBarButton>
            }
          >
            {tab === "visit" ? (
              <PivotVisitTable
                groups={visitsData?.groups ?? []}
                grandTotal={visitsData?.summary.grand_total ?? EMPTY_VISIT_TOTALS}
                totalRowCount={visitsData?.summary.row_count ?? 0}
                missedCount={visitsData?.summary.missed_count ?? 0}
                grid={
                  // Before the first response, cap the placeholder grid the way the API does
                  // (grid_max_days = 45) so a long range doesn't render hundreds of skeleton columns.
                  visitsData?.grid ?? {
                    date_from: (filters.dateTo.subtract(44, "day").isAfter(filters.dateFrom)
                      ? filters.dateTo.subtract(44, "day")
                      : filters.dateFrom
                    ).format("YYYY-MM-DD"),
                    date_to: filters.dateTo.format("YYYY-MM-DD"),
                    day_count: 0,
                    is_truncated: false,
                    max_days: 45,
                  }
                }
                groupLabel={groupLabel}
                unit={unit}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={onSort}
                collapsed={collapsed}
                onToggleGroup={toggleGroup}
                loading={loading}
              />
            ) : tab === "sku" ? (
              <PivotGroupedTable
                groups={productsData?.groups ?? []}
                grandTotal={productsData?.summary.grand_total ?? EMPTY_METRICS}
                totalRowCount={productsData?.summary.row_count ?? 0}
                groupLabel={groupLabel}
                unit={unit}
                identityHeader={[
                  { label: "ลำดับสินค้า", sortKey: "sequence" },
                  "รหัสสินค้า",
                  "ชื่อสินค้า",
                ]}
                renderIdentity={(row) => [
                  row.sequence ?? "—",
                  <CodeChip>{row.product_code}</CodeChip>,
                  row.product_name ?? "—",
                ]}
                getRowKey={(row) => row.product_code}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={onSort}
                collapsed={collapsed}
                onToggleGroup={toggleGroup}
                loading={loading}
                onRowClick={(row, group) => productDrill.toggle(group.key, row.product_code)}
                getRowActionLabel={(row) =>
                  `ดูช่องทางและร้านที่ขาย ${row.product_name ?? row.product_code}`
                }
                isRowExpanded={(row, group) => productDrill.isExpanded(group.key, row.product_code)}
                renderRowDetail={(row, group, flat) => (
                  <ProductStoresRows
                    entry={productDrill.entry(row.product_code)}
                    productCode={row.product_code}
                    groupKey={group.key}
                    isFlat={flat}
                    closedChannels={productDrill.closedChannels}
                    onToggleChannel={productDrill.toggleChannel}
                    onExport={() => onExportProductStores(row.product_code)}
                    exporting={exportingProduct === row.product_code}
                  />
                )}
              />
            ) : (
              <PivotGroupedTable
                groups={storesData?.groups ?? []}
                grandTotal={storesData?.summary.grand_total ?? EMPTY_METRICS}
                totalRowCount={storesData?.summary.row_count ?? 0}
                groupLabel={groupLabel}
                unit={unit}
                identityHeader={["รหัสร้านค้า", "ชื่อร้าน"]}
                renderIdentity={(row) => [
                  <CodeChip>{row.customer_code}</CodeChip>,
                  row.customer_name,
                ]}
                getRowKey={(row) => row.customer_code}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={onSort}
                collapsed={collapsed}
                onToggleGroup={toggleGroup}
                loading={loading}
                skippedRowCount={storesData?.summary.skipped_row_count}
                onRowClick={onStoreRowClick}
                extraColumns={tab === "route" ? visitTimeColumns : undefined}
                getRowActionLabel={(row) => `ดูรายสินค้าของร้าน ${row.customer_name}`}
              />
            )}
          </PivotCard>
        </>
      )}
    </Box>
  );
}
